import { createModelSchema } from '../../../common/createModelSchema';
import { trpc } from '../../lib/trpc';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  SegmentedControl,
  type SegmentedControlItem,
  Space,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { BadgeAlert, BadgeCheck } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';

const typeValues: SegmentedControlItem[] = [
  { label: 'Collection', value: 'collection' },
  { label: 'Singleton', value: 'singleton' },
];

export const Wizard = ({ modalId }: { readonly modalId: string }) => {
  const { control, formState, handleSubmit, register } = useForm({
    defaultValues: {
      name: '',
      schema: { fields: [], version: 1 as const },
      type: 'collection',
    },
    resolver: zodResolver(createModelSchema),
  });
  const { mutateAsync: createModelAsync } = trpc.models.create.useMutation();
  const utils = trpc.useUtils();

  const submitHandler = handleSubmit(async (data) => {
    try {
      await createModelAsync(data);

      notifications.show({
        message: (
          <>
            <BadgeCheck stroke="var(--color-lime-400)" /> Content type created
          </>
        ),
      });
      utils.models.invalidate();
      modals.close(modalId);
    } catch {
      notifications.show({
        message: (
          <>
            <BadgeAlert stroke="var(--color-red-400)" /> An error occurred.
            Please try again.
          </>
        ),
      });
    }
  });

  return (
    <form onSubmit={submitHandler}>
      <TextInput
        label="Name"
        {...register('name')}
        error={formState.errors.name?.message}
      />
      <Space h="lg" />
      <Controller
        control={control}
        name="type"
        render={({ field: { onChange, value } }) => (
          <SegmentedControl
            data={typeValues}
            onChange={onChange}
            radius="xl"
            size="sm"
            value={value}
          />
        )}
      />
      <Space h="lg" />
      <Button type="submit">Create</Button>
    </form>
  );
};
